import type { DashboardView } from "../../redux/portfolio.types"
import {
  createEmptyFilterLabels,
  createEmptyFilters
} from "../../redux/globalFilters"

/**
 * The GC Default view.
 *
 * WHY THIS EXISTS AT ALL. Home used to render ONE composition whatever view
 * was selected — the saved view chose which widgets the grid held, never how
 * the page was arranged. So a "new default layout" had nowhere to live: every
 * account landed on `normalizeToV3`'s fallback, literally named "My View",
 * showing the legacy arrangement. GC Default is the first view whose identity
 * selects a COMPOSITION, not just a widget set.
 *
 * It is a real, selectable, persisted view — not a flag and not a branch on
 * the user's role. Selecting it writes `activeViewId` through the same
 * `dashboard_config` path every other view uses, so switching away and back
 * behaves exactly like any saved view.
 */
export const GC_DEFAULT_VIEW_ID = "gc_default"
export const GC_DEFAULT_VIEW_NAME = "GC Default"

export const isGcDefaultView = (viewId: string | null | undefined): boolean =>
  viewId === GC_DEFAULT_VIEW_ID

const makeGcDefaultView = (): DashboardView => ({
  id: GC_DEFAULT_VIEW_ID,
  key: GC_DEFAULT_VIEW_ID,
  name: GC_DEFAULT_VIEW_NAME,
  order: [],
  hidden: [],
  globalFilters: createEmptyFilters(),
  filterLabels: createEmptyFilterLabels()
})

/**
 * Did this account ever save a dashboard layout of its own?
 *
 * Decides whether GC Default is merely OFFERED or actually SELECTED. A new
 * account gets the new default; an existing one keeps whatever view it was
 * last on, because silently moving somebody off their own arrangement is the
 * one thing the brief rules out. They can pick GC Default from the view menu.
 */
export const hasStoredLayout = (raw: unknown): boolean => {
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) return false
  const obj = raw as Record<string, unknown>
  if (Array.isArray(obj.views) && obj.views.length > 0) return true
  if (Array.isArray(obj.tiles) && obj.tiles.length > 0) return true
  if (Array.isArray(obj.order) && obj.order.length > 0) return true
  return false
}

/**
 * Puts GC Default in the view list without disturbing what is already there.
 *
 * Appended, never prepended over a personal view, and never replacing one: an
 * existing `gc_default` entry (the user has selected it before, so it is
 * persisted with their own order/geometry) wins over this fresh one.
 */
export const withGcDefaultView = (
  views: DashboardView[],
  activeViewId: string | null,
  storedLayout: boolean
): { views: DashboardView[]; activeViewId: string | null } => {
  // A fresh account IS GC Default — `normalizeToV3`'s "My View" is only a
  // placeholder for an absent config, not a view anybody made, so it is
  // replaced rather than listed beside the real default.
  if (!storedLayout) {
    return { views: [makeGcDefaultView()], activeViewId: GC_DEFAULT_VIEW_ID }
  }
  const present = views.some((view) => view.id === GC_DEFAULT_VIEW_ID)
  const next = present ? views : [...views, makeGcDefaultView()]
  // An account that has saved anything keeps its own active view, even when
  // that view is the legacy arrangement. GC Default is merely offered.
  return { views: next, activeViewId: activeViewId ?? next[0]?.id ?? null }
}
